import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HardHat, ShieldAlert } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { SAFETY_DOC_STATUS, fmtDate, isOverdue } from "../../lib/labels";
import { useUsers, userName } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { ActionButtons, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const SAFETY_DOC_TYPES = {
  plan_seguridad: "Plan de Seguridad y Salud", estudio_seguridad: "Estudio de Seguridad y Salud",
  certificado_maquinaria: "Certificado de maquinaria", reconocimiento_medico_generico: "Acreditación de aptitud (sin datos médicos)",
  formacion_prl: "Formación PRL", seguro_rc: "Póliza RC", libro_ordenes: "Libro de órdenes",
  autorizacion_trabajos: "Autorización de trabajos",
};

const BLOCK_LABELS = { certificaciones: "Bloquea certificaciones", gates: "Bloquea puertas de fase", informativo: "Solo alerta" };

export default function Seguridad() {
  const [pid, picker] = useProjectPicker();
  const users = useUsers();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ mandatory: true, block: "informativo", doc_type: "plan_seguridad" });
  const { data } = useQuery({
    queryKey: ["ops-safety", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/seguridad`)).data,
  });
  const scan = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/seguridad/escanear`)).data,
    onSuccess: (d) => { toast.success(`Escaneo completado: ${d.count} documento(s) caducados`); qc.invalidateQueries({ queryKey: ["ops-safety", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/seguridad`, f)).data,
    onSuccess: () => { toast.success("Documento SyS registrado"); setOpen(false); setF({ mandatory: true, block: "informativo", doc_type: "plan_seguridad" }); qc.invalidateQueries({ queryKey: ["ops-safety", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const docs = data?.documents || [];
  return (
    <div className="space-y-4" data-testid="seguridad-page">
      <PageHeader title="Seguridad y Salud"
        subtitle="Control documental mínimo: validez, revisión, caducidad y autorizaciones. Un documento obligatorio caducado activa el bloqueo configurado. Sin datos médicos ni sensibles."
        actions={<div className="flex items-center gap-2">{picker}
          <Button size="sm" variant="outline" onClick={() => scan.mutate()} disabled={scan.isPending} data-testid="safety-scan">Escanear caducidades</Button>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="create-safety-open">Nuevo documento</Button></div>}
        testid="seguridad-title" />
      {data?.safety_block && (
        <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="safety-block-banner">
          <ShieldAlert className="h-4 w-4" />
          <span><strong>Bloqueo activo ({BLOCK_LABELS[data.safety_block]}):</strong> {data.safety_block_reason}</span>
        </div>
      )}
      {!docs.length ? <EmptyState title="Sin documentación SyS" description="Registra el primer documento requerido." /> : (
        <Table data-testid="safety-table">
          <TableHeader><TableRow>
            <TableHead>Documento</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead>Obligatorio</TableHead>
            <TableHead>Bloqueo si caduca</TableHead><TableHead>Caducidad</TableHead><TableHead>Referencia</TableHead>
            <TableHead>Revisado por</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id} data-testid={`safety-row-${d.id}`}>
                <TableCell className="max-w-56"><p className="font-medium text-slate-800">{d.title}</p>
                  <p className="text-xs text-slate-400">{d.holder || ""}</p></TableCell>
                <TableCell className="text-xs">{SAFETY_DOC_TYPES[d.doc_type]}</TableCell>
                <TableCell><Chip map={SAFETY_DOC_STATUS} value={d.status} testid={`safety-status-${d.id}`} /></TableCell>
                <TableCell>{d.mandatory ? <ToneChip label="Obligatorio" tone="warning" testid={`safety-mandatory-${d.id}`} /> : <span className="text-xs text-slate-400">Opcional</span>}</TableCell>
                <TableCell className="text-xs">{d.mandatory ? BLOCK_LABELS[d.block] : "—"}</TableCell>
                <TableCell className={`text-xs ${isOverdue(d.expiry_date) ? "font-medium text-red-600" : ""}`} data-testid={`safety-expiry-${d.id}`}>{fmtDate(d.expiry_date)}</TableCell>
                <TableCell className="max-w-40 truncate text-xs text-slate-500">{d.document_ref || "—"}</TableCell>
                <TableCell className="text-xs">{d.reviewed_by ? userName(users.map, d.reviewed_by) : "—"}</TableCell>
                <TableCell><ActionButtons item={d} endpoint={`/proyectos/${pid}/ops/seguridad`} invalidate={[["ops-safety", pid]]} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-safety-dialog">
          <DialogHeader><DialogTitle>Nuevo documento SyS</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo *</Label>
              <Select value={f.doc_type || "plan_seguridad"} onValueChange={(v) => setF({ ...f, doc_type: v })}>
                <SelectTrigger data-testid="create-safety-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SAFETY_DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Título</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-safety-title" /></div>
            <div><Label>Titular / empresa</Label><Input value={f.holder || ""} onChange={(e) => setF({ ...f, holder: e.target.value })} data-testid="create-safety-holder" /></div>
            <div><Label>Caducidad</Label><Input type="date" value={f.expiry_date?.slice(0, 10) || ""} onChange={(e) => setF({ ...f, expiry_date: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="create-safety-expiry" /></div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} data-testid="create-safety-mandatory" /> Documento obligatorio
            </label>
            {f.mandatory && (
              <div><Label>Bloqueo si caduca</Label>
                <Select value={f.block} onValueChange={(v) => setF({ ...f, block: v })}>
                  <SelectTrigger data-testid="create-safety-block"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(BLOCK_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button data-testid="create-safety-submit" disabled={mut.isPending} onClick={() => mut.mutate()}>Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
